import React, { useState, useMemo, useEffect, useRef } from 'react';
import Highcharts from 'highcharts/highmaps';
import HighchartsReact from 'highcharts-react-official';
import { ASSAM_GEOJSON, HC_NAME_MAP } from '../../data/mainDashboardMockData';
import { fetchDistrictAvailability } from '../../data/api';
import DistrictStoreModal from '../DistrictStoreModal';

const TAB_LABELS = {
  all: 'All Facilities', ddwh: 'DDWH', mc: 'Medical Colleges',
  dh: 'District Hospitals', sdh: 'SDH', chc: 'CHC', phc: 'PHC', other: 'Other Facilities'
};

const AssamMap = () => {
  const [currentFacType, setCurrentFacType] = useState('all');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);
  const chartRef = useRef(null);

  // Re-fetch data whenever facility type tab changes
  useEffect(() => {
    setLoading(true);
    fetchDistrictAvailability(currentFacType === 'all' ? '' : currentFacType)
      .then(r => { setDistricts(r.data); setLoading(false); })
      .catch(e => { console.error(e); setLoading(false); });
  }, [currentFacType]);

  useEffect(() => {
    const handleResize = () => { if (chartRef.current) chartRef.current.reflow(); };
    window.addEventListener('resize', handleResize);
    const timer = setTimeout(handleResize, 300);
    return () => { window.removeEventListener('resize', handleResize); clearTimeout(timer); };
  }, []);

  const getColor = (val) => {
    if (val >= 90) return '#27ae60';
    if (val >= 75) return '#f39c12';
    return '#e74c3c';
  };

  const mapData = useMemo(() => {
    if (!districts.length) return [];
    // Group by GeoJSON name to aggregate districts that map to the same polygon
    const grouped = {};
    districts.forEach(d => {
      const val = parseFloat(d.avail);
      const districtForMap = d.district || d.n;
      const hcName = HC_NAME_MAP[districtForMap] || districtForMap;
      if (!grouped[hcName]) {
        grouped[hcName] = {
          name: hcName,
          labels: [],
          apiNames: [],
          facilities: [],
          totalStores: 0, storesAbove90: 0, stockoutStores: 0,
          availSum: 0, count: 0
        };
      }
      const g = grouped[hcName];
      g.labels.push(d.n || districtForMap);
      g.apiNames.push(districtForMap);
      if (d.facility) g.facilities.push(d.facility);
      g.totalStores += parseInt(d.total_stores) || 0;
      g.storesAbove90 += parseInt(d.stores_above_90) || 0;
      g.stockoutStores += parseInt(d.stockout_stores) || 0;
      g.availSum += val;
      g.count += 1;
    });
    const hasFacilityNames = ['ddwh','mc','dh','sdh','chc','phc'].includes(currentFacType);
    return Object.values(grouped).map(g => {
      const avgAvail = g.count > 0 ? g.availSum / g.count : 0;
      let label;
      if (hasFacilityNames && g.count <= 5) {
        label = g.labels.join(', ');
      } else if (hasFacilityNames) {
        label = g.apiNames[0] + ' (' + g.count + ')';
      } else {
        label = g.labels.join(', ');
      }
      return {
        name: g.name,
        value: parseFloat(avgAvail.toFixed(2)),
        color: getColor(avgAvail),
        districtLabel: label,
        districtApiName: g.apiNames[0],
        facilityName: hasFacilityNames ? g.labels.join(', ') : null,
        totalStores: g.totalStores,
        storesAbove90: g.storesAbove90,
        stockoutStores: g.stockoutStores
      };
    });
  }, [districts]);

  const handleDistrictClick = (districtName) => {
    setSelectedDistrict(districtName);
  };

  const mapOptions = useMemo(() => ({
    chart: {
      map: ASSAM_GEOJSON,
      backgroundColor: 'transparent',
      margin: [0, 0, 0, 0],
      spacing: [0, 0, 0, 0],
      animation: { duration: 300 },
      reflow: true
    },
    title: { text: '' },
    credits: { enabled: false },
    mapNavigation: { enabled: false },
    colorAxis: { visible: false, min: 0, max: 100 },
    legend: { enabled: false },
    tooltip: {
      useHTML: true,
      formatter: function () {
        var p = this.point;
        var facLabel = TAB_LABELS[currentFacType] || 'All Facilities';
        var isItemLevel = ['ddwh','mc','dh','sdh','chc','phc'].indexOf(currentFacType) !== -1;
        var facNames = p.facilityName ? p.facilityName.split(', ') : [];
        var facHtml = '';
        if (facNames.length > 0 && facNames.length <= 3) {
          facHtml = '<div style="font-size:10px;color:#1e3a5f;margin-bottom:4px;font-weight:600">' + p.facilityName + '</div>';
        } else if (facNames.length > 3) {
          facHtml = '<div style="font-size:10px;color:#1e3a5f;margin-bottom:4px;font-weight:600">' + facNames.length + ' facilities</div>';
        }
        return '<div style="padding:6px 2px;min-width:190px;max-width:320px">' +
          '<div style="font-weight:700;font-size:13px;margin-bottom:4px;padding-bottom:6px;border-bottom:1px solid #e8ecf1">' +
          (p.districtLabel || p.name) + '</div>' +
          facHtml +
          '<div style="font-size:9px;color:#3b82f6;font-weight:600;margin-bottom:6px;text-transform:uppercase;letter-spacing:.5px">' + facLabel + '</div>' +
          '<table style="border-collapse:collapse;width:100%">' +
          '<tr><td style="padding:2px 8px 2px 0;color:#6b7c93;font-size:11px">Availability</td><td style="font-weight:700;text-align:right;color:' + (p.value >= 90 ? '#27ae60' : p.value >= 75 ? '#f39c12' : '#e74c3c') + '">' + p.value + '%</td></tr>' +
          '<tr><td style="padding:2px 8px 2px 0;color:#6b7c93;font-size:11px">' + (isItemLevel ? 'Total Items' : 'Total Stores') + '</td><td style="font-weight:700;text-align:right">' + (p.totalStores || '-') + '</td></tr>' +
          '<tr><td style="padding:2px 8px 2px 0;color:#6b7c93;font-size:11px">' + (isItemLevel ? 'Items In Stock' : 'Stores &ge; 90%') + '</td><td style="font-weight:700;text-align:right;color:#27ae60">' + (p.storesAbove90 || '0') + '</td></tr>' +
          (isItemLevel ? '<tr><td style="padding:2px 8px 2px 0;color:#6b7c93;font-size:11px">Items Out of Stock</td><td style="font-weight:700;text-align:right;color:#e74c3c">' + (p.stockoutStores || '0') + '</td></tr>' : '') +
          '</table>' +
          '<div style="margin-top:8px;padding-top:6px;border-top:1px solid #e8ecf1;font-size:10px;color:#3b82f6;font-weight:600;text-align:center;cursor:pointer">Click to view details &rarr;</div>' +
          '</div>';
      }
    },
    plotOptions: {
      series: {
        cursor: 'pointer',
        point: {
          events: {
            click: function () {
              const apiName = this.districtApiName || this.districtLabel || this.name;
              handleDistrictClick(apiName);
            }
          }
        }
      }
    },
    series: [{
      data: mapData,
      joinBy: ['NAME', 'name'],
      name: 'Stock Availability',
      borderWidth: 1.5,
      borderColor: '#ffffff',
      nullColor: '#e2e8f0',
      states: { hover: { borderColor: '#1e293b', borderWidth: 2.5, brightness: 0.08 } },
      dataLabels: {
        enabled: true,
        format: '{point.districtLabel}',
        style: {
          fontSize: '7.5px', fontWeight: '700', color: '#1e293b',
          textOutline: '2px white', fontFamily: "'Segoe UI', system-ui, sans-serif"
        }
      }
    }]
  }), [mapData, currentFacType]);

  const tabs = [
    { id: 'all', label: 'All Facilities' },
    { id: 'ddwh', label: 'DDWH' },
    { id: 'mc', label: 'Medical Colleges' },
    { id: 'dh', label: 'District Hospitals' },
    { id: 'sdh', label: 'SDH' },
    { id: 'chc', label: 'CHC' },
    { id: 'phc', label: 'PHC' },
    { id: 'other', label: 'Other Facilities' }
  ];

  // Summary stats for selected filter
  const totalStores = districts.reduce((s, d) => s + (parseInt(d.total_stores) || 0), 0);
  const avgAvail = districts.length ? (districts.reduce((s, d) => s + parseFloat(d.avail), 0) / districts.length).toFixed(1) : '0';

  return (
    <>
      <div className={`map-section ${isFullscreen ? 'fullscreen' : ''}`} id="mapSection">
        <div className="map-header">
          <h3>Stock Availability by District</h3>
          <div className="map-actions">
            <button className="fs-btn" onClick={() => setIsFullscreen(!isFullscreen)} title="Toggle fullscreen">&#9974;</button>
            <span className={`info-icon-wrap ${showTooltip ? 'active' : ''}`} onClick={() => setShowTooltip(!showTooltip)}>
              <i className="info-icon">i</i>
              <div className="info-tooltip">
                <strong>About This Heatmap</strong>
                This heatmap displays the stock availability across all districts of Assam. Districts are colour-coded based on availability:<br /><br />
                <b style={{color:'#27ae60'}}>Green (&ge;90%)</b> — Good stock availability<br />
                <b style={{color:'#f39c12'}}>Amber (75–89%)</b> — Moderate, needs attention<br />
                <b style={{color:'#e74c3c'}}>Red (&lt;75%)</b> — Critical, immediate action required<br /><br />
                Use the <b>facility type tabs</b> to filter by DDWH, Medical Colleges, etc.<br />
                <b>Click on any district</b> to view store-level details.
              </div>
            </span>
          </div>
        </div>

        <div className="fac-tabs" id="facTabs">
          {tabs.map((tab) => (
            <button key={tab.id}
              className={`fac-tab ${currentFacType === tab.id ? 'active' : ''}`}
              onClick={() => setCurrentFacType(tab.id)}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filter summary bar */}
        {currentFacType !== 'all' && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 16, padding: '6px 16px',
            background: '#f0f7ff', borderBottom: '1px solid #dbeafe', fontSize: '.75rem'
          }}>
            <span style={{ fontWeight: 700, color: '#1e3a5f' }}>
              Showing: {TAB_LABELS[currentFacType]}
            </span>
            <span style={{ color: '#64748b' }}>
              {districts.length} districts · {totalStores} stores · Avg {avgAvail}%
            </span>
            {loading && <span style={{ color: '#3b82f6', fontWeight: 600 }}>Loading...</span>}
          </div>
        )}

        <div id="mapContainer" style={{ height: isFullscreen ? 'calc(100vh - 120px)' : '400px', position: 'relative' }}>
          {loading && (
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(255,255,255,0.7)', zIndex: 5,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '.85rem', color: '#3b82f6', fontWeight: 600
            }}>
              Loading {TAB_LABELS[currentFacType]} data...
            </div>
          )}
          {mapData.length > 0 ? (
            <HighchartsReact
              key={`${isFullscreen}-${currentFacType}`}
              highcharts={Highcharts}
              constructorType={'mapChart'}
              options={mapOptions}
              callback={(chart) => { chartRef.current = chart; }}
              containerProps={{ style: { height: '100%', width: '100%' } }}
            />
          ) : !loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', flexDirection: 'column', gap: 8 }}>
              <span style={{ fontSize: '2rem' }}>📭</span>
              <span>No availability data for <b>{TAB_LABELS[currentFacType]}</b> facilities</span>
            </div>
          ) : null}
        </div>

        <div className="map-legend">
          <div><span className="dot" style={{ background: '#27ae60' }}></span> &ge;90% &mdash; Good</div>
          <div><span className="dot" style={{ background: '#f39c12' }}></span> 75&ndash;89% &mdash; Moderate</div>
          <div><span className="dot" style={{ background: '#e74c3c' }}></span> &lt;75% &mdash; Critical</div>
        </div>
      </div>

      {selectedDistrict && (
        <DistrictStoreModal
          district={selectedDistrict}
          mode="avail"
          facType={currentFacType}
          onClose={() => setSelectedDistrict(null)}
        />
      )}
    </>
  );
};

export default AssamMap;
